import { Component, inject, computed, OnInit, OnDestroy, signal } from '@angular/core';
import { Motive } from './evaluation.model';
import { UiSectionHeaderComponent } from '../../shared/components/ui-section-header/ui-section-header.component';
import { MotiveEditorComponent } from './components/motive-editor/motive-editor.component';
import { DeleteConfirmationModalComponent } from '../../shared/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { EvaluationStore } from './evaluation.store';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-evaluation',
  imports: [
    UiSectionHeaderComponent,
    MotiveEditorComponent,
    DeleteConfirmationModalComponent,
    TranslateModule,
    MatIconModule,
  ],
  providers: [EvaluationStore],
  templateUrl: './evaluation.component.html',
  styleUrl: './evaluation.component.scss',
})
export class EvaluationComponent implements OnInit, OnDestroy {
  private readonly store = inject(EvaluationStore);
  private saveListener?: (event: Event) => void;

  // Public computed for template
  readonly config = this.store.currentConfig;
  readonly isLoading = this.store.isLoading;
  readonly error = this.store.error;
  readonly currentLanguage = computed(() => this.store.selectedLanguage());
  readonly availableLanguages = this.store.availableLanguages;

  readonly negativeMotives = computed(() => this.config()?.negativeMotives ?? []);
  readonly positiveMotives = computed(() => this.config()?.positiveMotives ?? []);
  readonly threshold = computed(() => this.config()?.positiveRatingStartingFrom ?? 4);

  showDeleteModal = signal(false);
  itemToDelete = signal<{ type: 'motive' | 'submotive'; data: any } | null>(null);

  // Delegate all actions to store
  onLanguageChange(language: string): void {
    this.store.setLanguage(language);
  }

  onThresholdChange(threshold: number): void {
    this.store.updateThreshold(threshold);
  }

  onMotiveUpdate(data: {
    type: 'negative' | 'positive';
    motiveId: string;
    updates: Partial<Motive>;
  }): void {
    this.store.updateMotive(data.type, data.motiveId, data.updates);
  }

  onMotiveAdd(data: { type: 'negative' | 'positive'; motive: Motive }): void {
    this.store.addMotive(data.type, data.motive);
  }

  onMotiveDelete(data: { type: 'negative' | 'positive'; motiveId: string }): void {
    this.itemToDelete.set({ type: 'motive', data });
    this.showDeleteModal.set(true);
  }

  onSubMotiveAdd(data: {
    type: 'negative' | 'positive';
    motiveId: string;
    subMotive: Motive;
  }): void {
    this.store.addSubMotive(data.type, data.motiveId, data.subMotive);
  }

  onSubMotiveUpdate(data: {
    type: 'negative' | 'positive';
    motiveId: string;
    subMotiveId: string;
    updates: Partial<Motive>;
  }): void {
    this.store.updateSubMotive(data.type, data.motiveId, data.subMotiveId, data.updates);
  }

  onSubMotiveDelete(data: {
    type: 'negative' | 'positive';
    motiveId: string;
    subMotiveId: string;
  }): void {
    this.itemToDelete.set({ type: 'submotive', data });
    this.showDeleteModal.set(true);
  }

  onConfirmDelete(): void {
    const item = this.itemToDelete();
    if (!item) return;

    if (item.type === 'motive') {
      this.store.deleteMotiveDirectly(item.data.motiveId);
    } else {
      this.store.deleteSubMotiveDirectly(item.data.motiveId, item.data.subMotiveId);
    }
    this.closeDeleteModal();
  }

  onCancelDelete(): void {
    this.closeDeleteModal();
  }

  private closeDeleteModal(): void {
    this.showDeleteModal.set(false);
    this.itemToDelete.set(null);
  }

  onSave(): void {
    this.store.saveConfig();
  }

  ngOnInit(): void {
    this.saveListener = () => {
      this.onSave();
    };
    window.addEventListener('global-save-triggered', this.saveListener);
  }

  ngOnDestroy(): void {
    if (this.saveListener) {
      window.removeEventListener('global-save-triggered', this.saveListener);
    }
  }
}
